/**
 * Section showing projects user no longer has access to
 * Story 226: Projects Dashboard
 * Story 382: Unified repo access flow with auto-recheck on tab return
 */

import { useState, useCallback } from 'react';
import type { ProjectListItem } from '../../hooks/useProjects';
import { RepoAccessResolver } from './RepoAccessResolver';
import './InaccessibleSection.css';

interface InaccessibleSectionProps {
  projects: ProjectListItem[];
}

export function InaccessibleSection({ projects }: InaccessibleSectionProps) {
  const [selectedProject, setSelectedProject] = useState<string | null>(null);

  const handleClick = useCallback((projectSlug: string) => {
    setSelectedProject((prev) => (prev === projectSlug ? null : projectSlug));
  }, []);

  const handleSuccess = useCallback(() => {
    // Close the resolver - the query invalidation is handled by RepoAccessResolver
    setSelectedProject(null);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedProject(null);
  }, []);

  if (projects.length === 0) return null;

  const count = projects.length;
  const pluralized = count === 1 ? 'project' : 'projects';

  return (
    <div className="inaccessible-section">
      <div className="inaccessible-section__header">
        <svg
          className="inaccessible-section__icon"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M8 1.5c-2.363 0-4 1.69-4 3.75 0 .984.424 1.625.984 2.304l.214.253c.223.264.47.556.673.848.284.411.537.896.621 1.49a.75.75 0 0 1-1.484.211c-.04-.282-.163-.547-.37-.847a8.456 8.456 0 0 0-.542-.68c-.084-.1-.173-.205-.268-.32C3.201 7.75 2.5 6.766 2.5 5.25 2.5 2.31 4.863 0 8 0s5.5 2.31 5.5 5.25c0 1.516-.701 2.5-1.328 3.259-.095.115-.184.22-.268.319-.207.245-.383.453-.541.681-.208.3-.33.565-.37.847a.751.751 0 0 1-1.485-.212c.084-.593.337-1.078.621-1.489.203-.292.45-.584.673-.848.075-.088.147-.173.213-.253.561-.679.985-1.32.985-2.304 0-2.06-1.637-3.75-4-3.75ZM5.75 12h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1 0-1.5ZM6 15.25a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1-.75-.75Z" />
        </svg>
        <span className="inaccessible-section__title">
          {count} {pluralized} cannot be shown as you don't currently have access
        </span>
      </div>

      <div className="inaccessible-section__list">
        {projects.map((project) => (
          <div key={project.id} className="inaccessible-section__item-wrapper">
            <button
              className="inaccessible-section__item"
              onClick={() => handleClick(project.slug)}
              type="button"
            >
              <span className="inaccessible-section__item-name">{project.slug}</span>
            </button>
            {selectedProject === project.slug && (
              <div className="inaccessible-section__resolver">
                <RepoAccessResolver
                  mode="restore"
                  repoSlug={project.slug}
                  onSuccess={handleSuccess}
                  onClose={handleClose}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
